import { ReactElement, ReactNode, useEffect, useRef } from 'react';

type OverlayModalProps = {
  closeModal: () => void;
  children?: ReactNode;
  width?: string;
  isOpen?: boolean;
  // Lets the page behind the panel stay interactive (pan/zoom a map, click a
  // different marker) instead of the full-screen scrim swallowing every
  // pointer event. Off by default: form panels want the background inert.
  allowBackgroundInteraction?: boolean;
};

/**
 * Side Panel Container component for showcasing content on the side panel. Content can be rendered inside this panel as children.
 * @param props - React Props.
 * @returns - Side Panel Container Component.
 */
export default function SidePanelContainer(props: OverlayModalProps): ReactElement {
  const { isOpen, closeModal, children, width, allowBackgroundInteraction } = props;
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    if (isOpen) dialogRef.current?.showModal();
    else dialogRef.current?.close();
  }, [isOpen]);

  return (
    <div>
      <div
        className={`fixed top-0 right-0 w-full h-screen bg-background-600/5 z-50 ${
          allowBackgroundInteraction ? 'pointer-events-none' : ''
        }`}
      >
        <div
          className={`p-5 pb-20 ${
            width || 'w-full sm:w-1/2 lg:w-1/3 2xl:w-1/4'
          } fixed h-screen right-0 top-0 z-10 overflow-auto bg-background-900 ${
            allowBackgroundInteraction ? 'pointer-events-auto' : ''
          }`}
        >
          <button
            type="button"
            onClick={closeModal}
            aria-label="Close panel"
            className="material-icons-outlined absolute right-3 top-3 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/[0.06] text-[20px] leading-none text-gray-300 transition-colors hover:bg-white/[0.12] hover:text-white"
          >
            close
          </button>
          {children}
        </div>
      </div>
    </div>
  );
}
